// What the editor does with a model's proposal: which cells to ask for,
// how to cut them into the chunks the endpoint takes, and how a proposal
// lands in the draft without overwriting anything the office typed since.
// The claims made here ride the next save; a cell edited by hand after
// the proposal is the office's again and claims nothing.

import type {
  IntakeLanguage,
  IntakeText,
  IntakeTranslateResponse,
  IntakeTranslateRow,
  IntakeTranslateSkipReason,
  IntakeTranslationClaim,
} from "@duralex/shared";
import { templateLabels } from "../../lib/intakeLogic";
import { withText, type Draft, type Problem } from "./draft";

/** The endpoint's chunk: at most this many rows, this many characters of
    label text (context lines do not count). */
export const CHUNK_ROWS = 60;
export const CHUNK_CHARS = 6000;

export interface Claim extends IntakeTranslationClaim {
  /** What the model proposed; the claim lives while the cell still says it. */
  text: string;
  /** What the cell said before, for "Discard AI drafts"; absent = empty. */
  previous?: string;
}

export const cellKey = (key: string, lang: IntakeLanguage) => `${key}\u0000${lang}`;

/** The second-language cells as a run finds them: a proposal lands only
    on a cell nobody typed in meanwhile. */
export function cellsBefore(draft: Draft, lang: IntakeLanguage): Map<string, string | undefined> {
  return new Map(templateLabels(draft).map((l) => [l.key, l.text[lang]]));
}

/** The cells a run would fill: labels with English and without `lang`,
    plus - when asked - the ones whose English moved. Draft order. */
export function cellsToTranslate(
  draft: Draft,
  lang: IntakeLanguage,
  stale: string[],
  includeStale: boolean,
): string[] {
  const again = new Set(includeStale ? stale : []);
  return templateLabels(draft)
    .filter((l) => l.text.en.trim() && (!l.text[lang] || again.has(l.key)))
    .map((l) => l.key);
}

type Context = NonNullable<IntakeTranslateRow["context"]>;

function row(key: string, text: IntakeText, context: Context): IntakeTranslateRow {
  const given: Context = {};
  if (context.section?.trim()) given.section = context.section;
  if (context.question?.trim()) given.question = context.question;
  return Object.keys(given).length
    ? { key, text: text.en, context: given }
    : { key, text: text.en };
}

/** The rows in the draft's order, grouped so a question travels with its
    help text and options: the model reads them as one thing. */
function units(draft: Draft, wanted: Set<string>): IntakeTranslateRow[][] {
  const out: IntakeTranslateRow[][] = [];
  const one = (key: string, text: IntakeText, context: Context = {}) =>
    wanted.has(key) ? [row(key, text, context)] : [];
  for (const c of draft.categories) out.push(one(`cat.${c.code}`, c.label));
  for (const s of draft.sections) {
    out.push(one(`section.${s.id}`, s.title));
    for (const q of s.questions) {
      const about = { section: s.title.en, question: q.label.en };
      out.push([
        ...one(`q.${q.id}`, q.label, { section: s.title.en }),
        ...(q.help ? one(`q.${q.id}.help`, q.help, about) : []),
        ...(q.options ?? []).flatMap((o) => one(`opt.${q.id}.${o.value}`, o.label, about)),
      ]);
    }
  }
  for (const d of draft.checklist) out.push(one(`doc.${d.id}`, d.label));
  for (const c of draft.consents) out.push(one(`consent.${c.id}`, c.text));
  return out.filter((u) => u.length > 0);
}

const size = (rows: IntakeTranslateRow[]) => rows.reduce((n, r) => n + r.text.trim().length, 0);

/** The chunks a run sends, in order. A unit that would not fit opens the
    next chunk; a unit larger than a whole chunk is cut by rows. */
export function chunkRows(draft: Draft, keys: string[]): IntakeTranslateRow[][] {
  const chunks: IntakeTranslateRow[][] = [];
  let cur: IntakeTranslateRow[] = [];
  let chars = 0;
  const fits = (rows: IntakeTranslateRow[]) =>
    cur.length + rows.length <= CHUNK_ROWS && chars + size(rows) <= CHUNK_CHARS;
  const take = (rows: IntakeTranslateRow[]) => {
    cur.push(...rows);
    chars += size(rows);
  };
  const flush = () => {
    if (cur.length) chunks.push(cur);
    cur = [];
    chars = 0;
  };
  for (const unit of units(draft, new Set(keys))) {
    if (!fits(unit)) flush();
    if (fits(unit)) {
      take(unit);
      continue;
    }
    for (const r of unit) {
      if (!fits([r])) flush();
      take([r]);
    }
  }
  flush();
  return chunks;
}

/** A proposal lands only where the English is still what the model was
    given, byte for byte, and the cell still says what it said when the
    run began; a label reworded or translated by hand meanwhile keeps
    its cell. */
export function applyProposal(
  draft: Draft,
  lang: IntakeLanguage,
  rows: IntakeTranslateRow[],
  res: IntakeTranslateResponse,
  before: Map<string, string | undefined>,
): { draft: Draft; claims: Claim[] } {
  const sent = new Map(rows.map((r) => [r.key, r.text]));
  const next = structuredClone(draft);
  const claims: Claim[] = [];
  for (const l of templateLabels(next)) {
    const text = res.texts[l.key];
    const basedOn = res.basedOn[l.key];
    if (!text || !basedOn || sent.get(l.key) !== l.text.en) continue;
    if (before.get(l.key) !== l.text[lang]) continue;
    const previous = l.text[lang];
    claims.push({
      key: l.key,
      lang,
      from: res.from,
      basedOn,
      model: res.model,
      runId: res.runId,
      text,
      ...(previous !== undefined ? { previous } : {}),
    });
    l.text[lang] = text;
  }
  return { draft: next, claims };
}

/** The claims whose cell still says what the model proposed - one per
    cell, the latest run's: a proposal that comes back word for word
    would otherwise be claimed twice, which the server refuses. */
export function aliveClaims(draft: Draft, claims: Claim[]): Claim[] {
  const now = new Map(templateLabels(draft).map((l) => [l.key, l.text]));
  const last = new Map<string, Claim>();
  for (const c of claims) {
    if (now.get(c.key)?.[c.lang] === c.text) last.set(cellKey(c.key, c.lang), c);
  }
  return [...last.values()];
}

/** What the save carries: the server's claim schema is strict. */
export function toClaims(claims: Claim[]): IntakeTranslationClaim[] {
  return claims.map(({ text: _text, previous: _previous, ...claim }) => claim);
}

/** Every living proposal put back to what the cell said before it -
    every language of a label, when two runs wrote it. */
export function discardDrafts(draft: Draft, claims: Claim[]): Draft {
  const next = structuredClone(draft);
  const alive = new Map<string, Claim[]>();
  for (const c of aliveClaims(next, claims)) alive.set(c.key, [...(alive.get(c.key) ?? []), c]);
  for (const l of templateLabels(next)) {
    for (const c of alive.get(l.key) ?? []) {
      const restored = withText(l.text, c.lang, c.previous ?? "")[c.lang];
      if (restored === undefined) delete l.text[c.lang];
      else l.text[c.lang] = restored;
    }
  }
  return next;
}

/** The keys that moved between two drafts in one edit: a label that
    left and one that arrived saying the same - an option value or a new
    row's id retyped after a proposal landed. When the English moved with
    the id (a new row's id follows its label), the other languages alone
    say which row it is. */
export function renamedKeys(before: Draft, after: Draft): Map<string, string> {
  const was = new Map(templateLabels(before).map((l) => [l.key, l.text]));
  const now = new Map(templateLabels(after).map((l) => [l.key, l.text]));
  const gone = [...was].filter(([k]) => !now.has(k));
  const born = [...now].filter(([k]) => !was.has(k));
  const out = new Map<string, string>();
  if (!gone.length || !born.length) return out;
  const sig = (t: IntakeText, withEn: boolean) =>
    JSON.stringify(
      Object.entries(t)
        .filter(([l]) => withEn || l !== "en")
        .sort(),
    );
  for (const withEn of [true, false]) {
    const taken = new Set(out.values());
    const free = new Map<string, string[]>();
    for (const [k, t] of born) {
      const s = sig(t, withEn);
      if (taken.has(k) || s === "[]") continue;
      free.set(s, [...(free.get(s) ?? []), k]);
    }
    for (const [k, t] of gone) {
      if (out.has(k)) continue;
      const to = free.get(sig(t, withEn))?.shift();
      if (to) out.set(k, to);
    }
  }
  return out;
}

/** The claims and the read ticks re-keyed after a rename, so a cell a
    model wrote stays claimed under its new id. */
export function followRenames(
  moved: Map<string, string>,
  claims: Claim[],
  read: Set<string>,
): { claims: Claim[]; read: Set<string> } {
  const readOut = new Set(read);
  for (const c of claims) {
    const to = moved.get(c.key);
    if (!to) continue;
    const from = cellKey(c.key, c.lang);
    if (readOut.delete(from)) readOut.add(cellKey(to, c.lang));
  }
  return {
    claims: claims.map((c) => (moved.has(c.key) ? { ...c, key: moved.get(c.key)! } : c)),
    read: readOut,
  };
}

/** A consent a model wrote binds the client; the office reads it before
    it can be saved. */
export function machineConsentProblems(
  alive: Claim[],
  read: Set<string>,
  name: (lang: IntakeLanguage) => string,
): Problem[] {
  return alive
    .filter((c) => c.key.startsWith("consent.") && !read.has(cellKey(c.key, c.lang)))
    .map((c) => ({
      where: c.key.slice("consent.".length),
      message: `read the ${name(c.lang)} consent text a model wrote, then tick that you did`,
    }));
}

/** What a run came to, across its chunks: how many rows went out and
    landed, why the rest did not, what the server noted about the ones
    that did, and its words about the batch. */
export interface RunOutcome {
  planned: number;
  sent: number;
  landed: number;
  refused: Partial<Record<Exclude<IntakeTranslateSkipReason, "unknown_key">, number>>;
  /** Proposals the draft would not take: the cell or its English moved
      while the chunk was out. The server never sees these. */
  edited: number;
  /** terms: the firm's target-language words the proposals stepped around, each once. */
  notes: { glossary: number; sameAsSource: number; terms: string[] };
  warnings: string[];
  stopped: boolean;
}

export const emptyOutcome = (planned: number): RunOutcome => ({
  planned,
  sent: 0,
  landed: 0,
  refused: {},
  edited: 0,
  notes: { glossary: 0, sameAsSource: 0, terms: [] },
  warnings: [],
  stopped: false,
});

/** One chunk's reply folded in. A key the model invented is not a row
    the office sent and counts for nothing; a warning is said once. */
export function tally(
  o: RunOutcome,
  rows: IntakeTranslateRow[],
  res: IntakeTranslateResponse,
  landed: number,
): RunOutcome {
  const sent = new Set(rows.map((r) => r.key));
  const refused = { ...o.refused };
  let refusedHere = 0;
  for (const s of res.skipped) {
    if (s.reason === "unknown_key" || !sent.has(s.key)) continue;
    refused[s.reason] = (refused[s.reason] ?? 0) + 1;
    refusedHere += 1;
  }
  // The server notes every missed term of a label; the label is one.
  const notes = { ...o.notes, terms: [...o.notes.terms] };
  const glossaryKeys = new Set<string>();
  const sameKeys = new Set<string>();
  for (const n of res.notes) {
    if (!sent.has(n.key)) continue;
    if (n.note !== "glossary") {
      sameKeys.add(n.key);
      continue;
    }
    glossaryKeys.add(n.key);
    if (n.term && !notes.terms.includes(n.term)) notes.terms.push(n.term);
  }
  notes.glossary += glossaryKeys.size;
  notes.sameAsSource += sameKeys.size;
  return {
    ...o,
    sent: o.sent + rows.length,
    landed: o.landed + landed,
    refused,
    edited: o.edited + Math.max(0, rows.length - refusedHere - landed),
    notes,
    warnings: [...o.warnings, ...res.warnings.filter((w) => !o.warnings.includes(w))],
  };
}

/** The run in one line for the language's row: what landed, what was
    left alone and why (the serious reason first), the server's notes,
    and its warnings word for word. */
export function runOutcomeLine(o: RunOutcome): string {
  const r = o.refused;
  const noAnswer = (r.missing ?? 0) + (r.empty ?? 0);
  const why = [
    r.ssn ? `${r.ssn} came back asking for a Social Security number` : "",
    r.unchanged ? `${r.unchanged} came back in English` : "",
    noAnswer ? `${noAnswer} had no answer` : "",
    r.placeholder ? `${r.placeholder} mishandled a placeholder` : "",
    r.too_long ? `${r.too_long} came back too long` : "",
    o.edited ? `${o.edited} edited meanwhile` : "",
  ].filter(Boolean);
  const head =
    o.sent < o.planned
      ? o.stopped
        ? `Stopped after ${o.sent} of ${o.planned}; `
        : `${o.sent} of ${o.planned} sent; `
      : "";
  const left = o.sent - o.landed;
  const body =
    left === 0
      ? o.sent === 1
        ? "1 translated"
        : `all ${o.sent} translated`
      : `${o.landed} of ${o.sent} translated; ${left} left alone: ${why.join(", ")}`;
  const g = o.notes.glossary;
  const same = o.notes.sameAsSource;
  const shown = o.notes.terms.slice(0, 3);
  const more = o.notes.terms.length - shown.length;
  const terms = shown.length ? ` (${shown.join(", ")}${more ? `, and ${more} more` : ""})` : "";
  const notes = [
    g ? `${g} ${g === 1 ? "does" : "do"} not use the firm's wording${terms}` : "",
    same ? `${same} ${same === 1 ? "reads as its" : "read as their"} English` : "",
  ].filter(Boolean);
  return [`${head}${body}.`, notes.length ? `${notes.join("; ")}.` : "", ...o.warnings]
    .filter(Boolean)
    .join(" ");
}
